import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { customerApi } from '../../api/customers'
import { Customer, CustomerStatus, CustomerUpdateRequest } from '../../types'

export default function CustomerDetailPage() {
  const { customerId } = useParams()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<CustomerUpdateRequest>({})

  const fetchCustomer = async () => {
    if (!customerId) return
    try {
      setLoading(true)
      const data = await customerApi.getCustomer(customerId)
      setCustomer(data)
      setFormData({
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        note: data.note,
        status: data.status,
      })
    } catch (error) {
      console.error('Failed to fetch customer:', error)
      navigate('/customers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomer()
  }, [customerId])

  const handleSave = async () => {
    if (!customerId) return
    try {
      await customerApi.updateCustomer(customerId, formData)
      setIsEditing(false)
      fetchCustomer()
    } catch (error) {
      console.error('Failed to update customer:', error)
      alert('更新失敗')
    }
  }

  const handleDelete = async () => {
    if (!customerId || !window.confirm('確定要刪除此客戶嗎？此操作無法復原。')) return
    try {
      await customerApi.deleteCustomer(customerId)
      navigate('/customers')
    } catch (error) {
      console.error('Failed to delete customer:', error)
      alert('刪除失敗')
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  if (loading) return <div className="p-8 text-center">載入中...</div>
  if (!customer) return <div className="p-8 text-center">找不到客戶</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/customers')}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isEditing ? '編輯客戶' : customer.name}
          </h1>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="rounded-lg px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
              >
                <Save className="h-4 w-4" />
                儲存
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-red-600 hover:bg-red-50 dark:border-red-900 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4" />
                刪除
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
              >
                編輯
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">基本資訊</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  姓名
                </label>
                {isEditing ? (
                  <input
                    name="name"
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <div className="text-gray-900 dark:text-white">{customer.name}</div>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  狀態
                </label>
                {isEditing ? (
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value={CustomerStatus.ACTIVE}>啟用</option>
                    <option value={CustomerStatus.INACTIVE}>停用</option>
                    <option value={CustomerStatus.BLACKLISTED}>黑名單</option>
                  </select>
                ) : (
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      customer.status === CustomerStatus.ACTIVE
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : customer.status === CustomerStatus.INACTIVE
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                    }`}
                  >
                    {customer.status === CustomerStatus.ACTIVE
                      ? '啟用'
                      : customer.status === CustomerStatus.INACTIVE
                      ? '停用'
                      : '黑名單'}
                  </span>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  電話
                </label>
                {isEditing ? (
                  <input
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <div className="text-gray-900 dark:text-white">{customer.phone}</div>
                )}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                {isEditing ? (
                  <input
                    name="email"
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <div className="text-gray-900 dark:text-white">{customer.email || '-'}</div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  地址
                </label>
                {isEditing ? (
                  <input
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <div className="text-gray-900 dark:text-white">{customer.address || '-'}</div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  備註
                </label>
                {isEditing ? (
                  <textarea
                    name="note"
                    value={formData.note || ''}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-gray-300 p-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                ) : (
                  <div className="text-gray-900 dark:text-white">{customer.note || '-'}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats / Other Info */}
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">統計資訊</h3>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">總訂單數</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customer.total_orders}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">加入時間</div>
                <div className="text-sm text-gray-900 dark:text-white">
                  {format(new Date(customer.created_at), 'yyyy/MM/dd HH:mm')}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">最後更新</div>
                <div className="text-sm text-gray-900 dark:text-white">
                  {format(new Date(customer.updated_at), 'yyyy/MM/dd HH:mm')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
